import React, { useState } from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Card as RawCard } from "@/components/ui/card";
import { Switch as RawSwitch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calender";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ChevronLeft,
  Calendar as CalendarIcon,
  Clock,
  CheckCircle2,
  Minus,
  Plus,
  Car,
  ArrowRight,
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { createPageUrl } from "../../utils/index.ts";

/**
 * @param {{
 *   vehicle: {
 *     image: string;
 *     provider: string;
 *     vehicle_type: string;
 *     pickup_location: string;
 *     dropoff_location: string;
 *     duration_minutes: number;
 *     description: string;
 *     amenities: string[];
 *     available_times: string[];
 *     return_available_times?: string[];
 *     price: number;
 *     max_passengers: number;
 *     supports_round_trip?: boolean;
 *   };
 *   initialDate?: any;
 *   initialPassengers?: number;
 *   onBack: () => void;
 *   onProceedToCheckout: (date: any, returnDate: any, passengers: number, pickupTime: string, returnPickupTime: string, isRoundTrip: boolean) => void;
 * }} props
 */
export default function TransportDetail({
  vehicle,
  initialDate,
  initialPassengers,
  onBack,
  onProceedToCheckout,
}) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Card = /** @type {React.ComponentType<any>} */ (RawCard);
  const Switch = /** @type {React.ComponentType<any>} */ (RawSwitch);
  const PopoverContentComp = /** @type {React.ComponentType<any>} */ (PopoverContent);

  const [selectedDate, setSelectedDate] = useState(initialDate || null);
  const [returnDate, setReturnDate] = useState(/** @type {Date | null} */ (null));
  const [passengers, setPassengers] = useState(initialPassengers || 2);
  const [pickupTime, setPickupTime] = useState(/** @type {string | null} */ (null));
  const [returnPickupTime, setReturnPickupTime] = useState(/** @type {string | null} */ (null));
  const [isRoundTrip, setIsRoundTrip] = useState(false);

  const formattedDuration = `${Math.floor(vehicle.duration_minutes / 60)}h ${vehicle.duration_minutes % 60}m`;
  const totalAmount = vehicle.price * (isRoundTrip ? 2 : 1);

  const canProceed =
    selectedDate &&
    pickupTime &&
    (!isRoundTrip || (returnDate && returnPickupTime));

  const handleProceed = () => {
    if (canProceed) {
      onProceedToCheckout(
        selectedDate,
        returnDate,
        passengers,
        pickupTime || "",
        returnPickupTime || "",
        isRoundTrip
      );
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Header */}
      <div className="border-b border-gray-100 bg-white sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={onBack}
              className="gap-2 -ml-2 text-[#0B2421] hover:bg-transparent hover:text-[#0B2421]/70 hover:underline"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to Transport Search
            </Button>
            <Link to={createPageUrl("GolfBooking")}>
              <Button
                variant="ghost"
                className="gap-2 text-[#0B2421] hover:bg-transparent hover:text-[#0B2421]/70 hover:underline"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to Golf Booking
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl"
            >
              <img
                src={vehicle.image}
                alt={`${vehicle.provider} ${vehicle.vehicle_type}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <div className="flex items-center gap-2 mb-3">
                  <Car className="h-6 w-6" />
                  <span className="text-xl font-semibold">{vehicle.provider}</span>
                  <span className="ml-auto bg-[#D4AF37] text-[#0B2421] font-bold px-3 py-1 rounded-full text-sm">
                    {vehicle.vehicle_type}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-2xl font-bold">
                  <span className="text-lg">{vehicle.pickup_location}</span>
                  <ArrowRight className="h-6 w-6 text-[#D4AF37]" />
                  <span className="text-lg">{vehicle.dropoff_location}</span>
                </div>
                <div className="flex items-center gap-2 mt-2 text-white/80">
                  <Clock className="h-5 w-5" />
                  <span>Journey Time: {formattedDuration}</span>
                </div>
              </div>
            </motion.div>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-[#0B2421]">About This Service</h2>
                <p className="leading-relaxed text-gray-600 whitespace-pre-line">{vehicle.description}</p>
              </Card>
            </motion.div>

            {/* Amenities */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-[#0B2421]">Vehicle Amenities</h2>
                <div className="grid grid-cols-2 gap-3">
                  {vehicle.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600" />
                      <span className="text-gray-600">{amenity}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </motion.div>
          </div>

          {/* Right Column - Booking Widget */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="sticky top-24"
            >
              <Card className="p-6 bg-white border border-gray-100 rounded-2xl shadow-md">
                {/* Price */}
                <div className="mb-6">
                  <div className="text-sm mb-1 text-gray-500">Fixed Price</div>
                  <div className="text-4xl font-bold text-[#D4AF37]">
                    SGD {vehicle.price}
                  </div>
                </div>

                <div className="space-y-5">
                  {/* Round Trip Toggle */}
                  {vehicle.supports_round_trip && (
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-semibold text-[#0B2421]">Round Trip</label>
                      <Switch
                        checked={isRoundTrip}
                        onCheckedChange={setIsRoundTrip}
                      />
                    </div>
                  )}

                  {/* Travel Date */}
                  <div>
                    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                      Travel Date
                    </label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="w-full flex items-center justify-start gap-2 h-12 rounded-lg border border-gray-200 bg-white px-4 text-[#0B2421] hover:bg-gray-50"
                        >
                          <CalendarIcon className="h-4 w-4 text-gray-500" />
                          <span className={selectedDate ? "text-gray-700" : "text-gray-400"}>
                            {selectedDate ? format(selectedDate, "dd MMM yyyy") : "Choose a date"}
                          </span>
                        </button>
                      </PopoverTrigger>
                      <PopoverContentComp className="w-auto p-0" align="start">
                        <Calendar
                          className=""
                          classNames={{}}
                          mode="single"
                          selected={selectedDate}
                          onSelect={setSelectedDate}
                          disabled={/** @param {Date} d */ (d) => d < new Date()}
                        />
                      </PopoverContentComp>
                    </Popover>
                  </div>

                  {/* Passengers */}
                  <div>
                    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                      Number of Passengers
                    </label>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setPassengers(Math.max(1, passengers - 1))}
                        disabled={passengers <= 1}
                        className="h-12 w-12 bg-[#0B2421] hover:bg-[#0B2421]/85 border-none text-white"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <div className="flex-1 text-center">
                        <div className="text-2xl font-bold text-[#0B2421]">{passengers}</div>
                        <div className="text-xs text-gray-500">Passengers</div>
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setPassengers(Math.min(vehicle.max_passengers, passengers + 1))}
                        disabled={passengers >= vehicle.max_passengers}
                        className="h-12 w-12 bg-[#0B2421] hover:bg-[#0B2421]/85 border-none text-white"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Max {vehicle.max_passengers} passengers</p>
                  </div>

                  {/* Pickup Time */}
                  <div>
                    <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                      Pickup Time
                    </label>
                    <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                      {vehicle.available_times.map((time) => (
                        <Button
                          key={time}
                          variant={pickupTime === time ? "default" : "outline"}
                          onClick={() => setPickupTime(time)}
                          className={`h-12 ${
                            pickupTime === time
                              ? "bg-[#0B2421] text-white border-[#0B2421] hover:bg-[#0B2421]"
                              : "text-[#0B2421] bg-gray-50 border-gray-200 hover:bg-[#0B2421] hover:text-white"
                          }`}
                        >
                          <div className="flex flex-col items-center">
                            <Clock className="h-3 w-3 mb-1" />
                            <span className="text-xs">{time}</span>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Return Date (if round trip) */}
                  {isRoundTrip && (
                    <div>
                      <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                        Return Date
                      </label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="w-full flex items-center justify-start gap-2 h-12 rounded-lg border border-gray-200 bg-white px-4 text-[#0B2421] hover:bg-gray-50"
                          >
                            <CalendarIcon className="h-4 w-4 text-gray-500" />
                            <span className={returnDate ? "text-gray-700" : "text-gray-400"}>
                              {returnDate ? format(returnDate, "dd MMM yyyy") : "Choose return date"}
                            </span>
                          </button>
                        </PopoverTrigger>
                        <PopoverContentComp className="w-auto p-0" align="start">
                          <Calendar
                            className=""
                            classNames={{}}
                            mode="single"
                            selected={returnDate}
                            onSelect={setReturnDate}
                            disabled={/** @param {Date} d */ (d) => !selectedDate || d < selectedDate || d < new Date()}
                          />
                        </PopoverContentComp>
                      </Popover>
                    </div>
                  )}

                  {/* Return Pickup Time (if round trip) */}
                  {isRoundTrip && (
                    <div>
                      <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                        Return Pickup Time
                      </label>
                      <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                        {(vehicle.return_available_times || vehicle.available_times).map((time) => (
                          <Button
                            key={`return-${time}`}
                            variant={returnPickupTime === time ? "default" : "outline"}
                            onClick={() => setReturnPickupTime(time)}
                            className={`h-12 ${
                              returnPickupTime === time
                                ? "bg-[#0B2421] text-white border-[#0B2421] hover:bg-[#0B2421]"
                                : "text-[#0B2421] bg-gray-50 border-gray-200 hover:bg-[#0B2421] hover:text-white"
                            }`}
                          >
                            <div className="flex flex-col items-center">
                              <Clock className="h-3 w-3 mb-1" />
                              <span className="text-xs">{time}</span>
                            </div>
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Summary */}
                  <div className="pt-4 border-t border-gray-100 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Route</span>
                      <span className="font-medium text-[#0B2421] text-right text-xs">
                        {vehicle.pickup_location} → {vehicle.dropoff_location}
                      </span>
                    </div>
                    {selectedDate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Date</span>
                        <span className="font-medium text-[#0B2421]">
                          {format(selectedDate, "dd MMM yyyy")}
                        </span>
                      </div>
                    )}
                    {pickupTime && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Pickup</span>
                        <span className="font-medium text-[#0B2421]">{pickupTime}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Passengers</span>
                      <span className="font-medium text-[#0B2421]">{passengers}</span>
                    </div>
                    {isRoundTrip && returnDate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Return Date</span>
                        <span className="font-medium text-[#0B2421]">
                          {format(returnDate, "dd MMM yyyy")}
                        </span>
                      </div>
                    )}
                    {isRoundTrip && returnPickupTime && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Return Pickup</span>
                        <span className="font-medium text-[#0B2421]">{returnPickupTime}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-100">
                      <span className="text-[#0B2421]">Total</span>
                      <span className="text-[#D4AF37]">SGD {totalAmount}</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleProceed}
                    disabled={!canProceed}
                    className="w-full h-14 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold text-lg rounded-xl disabled:opacity-50"
                  >
                    Proceed to Secure Checkout
                  </Button>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
